import { Button, Card, CardBody } from '@heroui/react'
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

import { CSS_CLASSES } from '../constants'

interface ErrorFallbackProps {
  error?: Error
  resetError?: () => void
  title?: string
  message?: string
  showRetry?: boolean
  componentName?: string
}

export const ErrorFallback = ({
  error,
  resetError,
  title = 'Something went wrong',
  message,
  showRetry = true,
  componentName,
}: ErrorFallbackProps) => {
  const displayMessage =
    message || error?.message || 'An unexpected error occurred'

  const handleRetry = () => {
    if (resetError) {
      resetError()
    } else {
      // Fallback: refresh the page
      window.location.reload()
    }
  }

  return (
    <Card className={`${CSS_CLASSES.GLASSMORPHISM_BASE} max-w-md mx-auto`}>
      <CardBody className="text-center p-6">
        {/* Error Icon */}
        <div className="flex justify-center mb-4">
          <ExclamationTriangleIcon
            className={`w-16 h-16 ${CSS_CLASSES.TEXT_ERROR}`}
          />
        </div>

        {/* Error Title */}
        <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>

        {/* Error Message */}
        <p className="text-gray-600 mb-4">{displayMessage}</p>

        {/* Component Context (for debugging) */}
        {componentName && process.env.NODE_ENV === 'development' && (
          <p className="text-xs text-gray-400 mb-4">
            Component: {componentName}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {showRetry && (
            <Button
              color="primary"
              variant="solid"
              onClick={handleRetry}
              startContent={<ArrowPathIcon className="w-4 h-4" />}
            >
              Try Again
            </Button>
          )}

          <Button
            color="default"
            variant="light"
            onClick={() => (window.location.href = '/')}
            size="sm"
          >
            Go Home
          </Button>
        </div>

        {/* Technical Details (development only) */}
        {error && process.env.NODE_ENV === 'development' && (
          <details className="mt-4 text-left">
            <summary className="text-xs text-gray-400 cursor-pointer">
              Technical Details
            </summary>
            <pre className="text-xs text-gray-500 mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-32">
              {error.stack}
            </pre>
          </details>
        )}
      </CardBody>
    </Card>
  )
}
